/** "Am I a preview?" — and nothing else.
 *
 *  Split out of `preview.ts` because the two have very different audiences. This
 *  predicate is read on every page load by the age gate, the promo popup, the
 *  tracking pipeline and `App.vue`, so it belongs in the entry chunk. The channel
 *  it used to sit beside — the postMessage listener, the patch appliers, the
 *  click-to-select delegate — runs for nobody except a seller with the builder
 *  open, and now loads on demand.
 *
 *  Keeping them in one module put the whole channel in front of every shopper,
 *  and dragging `@dk/theme`'s patch helpers in behind it is what made the cost
 *  visible against a 70 kB budget.
 *
 *  Two shapes reach here through `?dk_preview=`:
 *
 *    dk_preview=1        the builder iframe, which drives the theme over
 *                        postMessage and owns the URL it set.
 *    dk_preview={token}  a minted preview of ONE unpublished theme — the seller's
 *                        theme library and the admin console's theme catalogue.
 *
 *  The value is read ONCE, here, at module load, and the token form is then
 *  wiped from the address bar (see `stripPreviewParam`). So nothing may ask
 *  `location` about it again: by the time a component mounts, the answer there
 *  is already gone.
 *
 *  The `typeof` guard is for the test runner, not the browser: most of this
 *  app's suite runs in vitest's default NODE environment, where there is no
 *  `location`, and a module that throws on import would take out every test that
 *  transitively reaches it. */
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router"

const raw =
  typeof location === "undefined" ? null : new URLSearchParams(location.search).get("dk_preview")

/** A forced A/B arm — `&dk_arm={experiment key}:{arm index}` beside a preview
 *  (docs/plans/ab-testing.md §3.4, seller QA). Honoured only in preview mode, so
 *  a forced session is also an untracked one: it is never exposed, never counted,
 *  and cannot skew the report it is checking. Read once, at module load, like the
 *  preview value; `dk_preview=1` is not stripped, so the link survives a reload. */
const armRaw =
  raw === null || typeof location === "undefined" ? null : new URLSearchParams(location.search).get("dk_arm")

export function forcedArm(): { key: string; arm: number } | null {
  const m = armRaw?.match(/^(exp_[0-9a-z]+):([0-2])$/)
  return m ? { key: m[1]!, arm: Number(m[2]) } : null
}

/** True for both shapes — this is the "do not track, do not pop up, let the
 *  theme be replaced live" switch, and both need it. */
export function isPreview(): boolean {
  return raw !== null
}

/** The minted token, or "" for the builder's `dk_preview=1` (which authorises
 *  nothing — the builder pushes its draft over postMessage) and for no preview. */
export function previewToken(): string {
  return raw !== null && raw !== "1" ? raw : ""
}

/** Drops `dk_preview={token}` from the address bar once it has been spent.
 *
 *  The token is a bearer grant to view an unpublished theme, and leaving it in the
 *  URL means every copied link, every screenshot and every "share this demo" hands
 *  it on — on `{key}.dikafy.com`, a public page the marketing site links to. It is
 *  also just noise in front of a store an admin is showing someone.
 *
 *  A reload therefore returns the LIVE storefront rather than the preview, which
 *  is the honest trade: the alternative is persisting a bearer token in storage,
 *  where it would outlive the tab and quietly show a draft to someone who came
 *  back to the same shop later. Re-press Preview to mint another.
 *
 *  `dk_preview=1` is left alone: that URL belongs to the builder's iframe, which
 *  reloads it, and it grants nothing. */
export function stripPreviewParam(): void {
  if (!previewToken()) return
  try {
    const url = new URL(location.href)
    url.searchParams.delete("dk_preview")
    history.replaceState(history.state, "", url.pathname + url.search + url.hash)
  } catch {
    // Sandboxed iframe / exotic scheme — the preview itself still works, and a
    // visible token is not worth failing a page load over.
  }
}

/** Router guard (`router.beforeEach`): a navigation whose query still carries a
 *  minted token goes to the same place without it, replacing history.
 *
 *  `stripPreviewParam` alone did not stick. The router captures the landing URL
 *  when it is created, and its first navigation commits with
 *  `history.replace(to.fullPath)` — token included — after the wipe, because that
 *  navigation waits on a lazy page chunk and on the bootstrap. The strip still
 *  runs first, so the address bar is clean from the first frame; this makes the
 *  router's own commit clean too.
 *
 *  `dk_preview=1` is the builder iframe's and grants nothing, so it stays; so does
 *  `dk_arm`, which previewFlag has already read. */
export function dropPreviewToken(to: RouteLocationNormalized): RouteLocationRaw | true {
  const t = to.query.dk_preview
  if (t == null || t === "1") return true
  const query = { ...to.query }
  delete query.dk_preview
  return { path: to.path, query, hash: to.hash, replace: true }
}
